import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit } from '@angular/core';
import { MaterialModule } from '@app/shared/modules/material/material.module';
import { RouterModule, Router } from '@angular/router';
import { GraphqlService } from '@app/core/services/graphql/graphql.service';
import { TransformedHeaderBlock } from '@app/shared/types/header.types';
import { PageAssembly } from '@app/shared/types/page-assembly.types';
import { AuthenticationService } from '@app/core/services/authentication/authentication.service';
import { BrowserCheckService } from '@app/utils/browser-check.service';

@Component({
  selector: 'rsc-asp-header',
  standalone: true,
  imports: [CommonModule, RouterModule, MaterialModule],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  animations: [
    trigger('slideToggle', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate(
          '300ms ease-in',
          style({ transform: 'translateY(0)', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms ease-out',
          style({ transform: 'translateY(-100%)', opacity: 0 }),
        ),
      ]),
    ]),
  ],
})
export class HeaderComponent implements OnInit {
  headerData: TransformedHeaderBlock = {};
  pageAssemblies: PageAssembly[] = [];
  menuText = 'Account';
  isMenuOpen = false;
  isAccountMenuOpen = false;
  isLoggedIn = false;

  constructor(
    private graphqlService: GraphqlService,
    private authService: AuthenticationService,
    private browserCheck: BrowserCheckService,
    private router: Router,
    private elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.fetchHeaderData();
    this.getAllSlugs();
    this.checkLoginStatus();
  }

  @HostListener('document:click', ['$event.target'])
  onClickOutside(targetElement: HTMLElement): void {
    const clickedInside = this.elementRef.nativeElement.contains(targetElement);
    if (!clickedInside && this.isAccountMenuOpen) {
      this.isAccountMenuOpen = false;
    }
  }

  fetchHeaderData(): void {
    this.graphqlService.getHeaderBlock().subscribe({
      next: (result: any) => {
        const items = result?.data?.headerBlockCollection?.items;
        if (items?.length) {
          this.headerData = this.transformHeaderData(items[0]);
        }
      },
      error: (error) => console.error(error),
    });
  }

  transformHeaderData(apiData: any): TransformedHeaderBlock {
    return {
      menuText: apiData?.menuText || 'Account',
      logoUrl: apiData?.logo?.url || '',
      logoAlt: apiData?.logo?.description || apiData?.logo?.title || '',
    };
  }

  getAllSlugs(): void {
    this.graphqlService.getAllPageAssemblySlugs().subscribe({
      next: (result: any) => {
        const raw: PageAssembly[] =
          result?.data?.pageAssemblyCollection?.items || [];
        const topLevel = raw.filter((item) => !item.slug?.includes('/'));
        // sort safely if order is missing
        topLevel.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        this.pageAssemblies = [
          { pageTitle: 'Home', slug: '', order: -Infinity },
          ...topLevel.map((item) => ({ ...item, slug: `${item.slug}` })),
        ];
      },
      error: (error) => {
        console.error('Error fetching page assembly slugs:', error);
      },
    });
  }

  checkLoginStatus(): void {
    if (this.browserCheck.isBrowser()) {
      this.isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
      this.menuText = this.isLoggedIn ? 'My Account' : 'Account';
    }
  }

  toggleMenu(): void {
    this.isMenuOpen = !this.isMenuOpen;
  }

  toggleAccountMenu(event?: Event): void {
    event?.preventDefault();
    this.isAccountMenuOpen = !this.isAccountMenuOpen;
  }

  private closeMobileMenu(): void {
    if (this.isMenuOpen) this.isMenuOpen = false;
  }

  onLoginClick(): void {
    this.closeMobileMenu();
    if (this.browserCheck.isBrowser()) {
      this.authService.redirectToLogin('');
    }
  }

  onSignupClick(): void {
    this.closeMobileMenu();
    if (this.browserCheck.isBrowser()) {
      window.location.assign(
        'https://secure.rsc.org.uk/account/login?ReturnUrl=%2Faccount%2Fupdate',
      );
    }
  }

  onLogoutClick(): void {
    this.closeMobileMenu();
    if (this.browserCheck.isBrowser()) {
      sessionStorage.removeItem('authToken');
      localStorage.removeItem('isLoggedIn');
      this.isLoggedIn = false;
      this.menuText = 'Account';
      if (this.router.url !== '/') {
        this.router.navigateByUrl('/');
      }
    }
  }

  navigateToPage(slug?: string, isProtected?: boolean): void {
    this.closeMobileMenu();

    if (!this.isLoggedIn && isProtected) {
      this.authService.redirectToLogin(slug);
      return;
    }

    const target = `/${slug ?? ''}`;
    if (this.router.url !== target) {
      this.router.navigateByUrl(target);
    }
  }
}
